import { LIB } from './library';
import { prefixFor, standardOf } from './standards';
import { SOLENOID_VALVES } from './fluid';
import type { Page, Project, PType, Sym } from './types';

let seq = 0;
export const newId = () => 'i' + Date.now().toString(36) + (++seq).toString(36) + Math.random().toString(36).slice(2, 5);

const allSyms = (pr: Project) => pr.pages.flatMap((p) => p.syms);

/** Next free device tag for a prefix, e.g. K1, K2 → K3. */
export function nextTag(pr: Project, prefix: string): string {
  let m = 0;
  const re = new RegExp('^' + prefix + '(\\d+)$');
  for (const s of allSyms(pr)) {
    const r = re.exec(s.tag || '');
    if (r) m = Math.max(m, +r[1]);
  }
  return prefix + (m + 1);
}

export function nextTerminal(pr: Project, strip: string): string {
  let m = 0;
  for (const s of allSyms(pr))
    if (s.type === 'term' && s.tag === strip) {
      const n = parseInt(s.props.pinNo || '', 10);
      if (n > m) m = n;
    }
  return String(m + 1);
}

export function nextPlcAddress(pr: Project, type: string): string {
  const n = allSyms(pr).filter((s) => s.type === type).length;
  if (type === 'plcai') return 'IW' + (64 + n * 2);
  if (type === 'plcao') return 'QW' + (64 + n * 2);
  return (type === 'plcin' ? 'I' : 'Q') + Math.floor(n / 8) + '.' + (n % 8);
}

export function ptypeFromName(n: string): PType {
  if (/^L\d?$/.test(n)) return 'L';
  if (n === 'N') return 'N';
  if (n === 'PE') return 'PE';
  if (/^\+|24V/.test(n)) return 'DC+';
  if (/^0V|^M$|^-/.test(n)) return 'DC-';
  return '';
}

/** Remembers the last main-function tag per prefix so new contacts join it. */
export type TagMemory = Record<string, string>;

export function createSymbol(pr: Project, page: Page, type: string, x: number, y: number, rot: number, mem: TagMemory): Sym {
  const L = { ...LIB[type], prefix: prefixFor(type, standardOf(pr)) };
  const s: Sym = { id: newId(), type, x, y, rot: rot || 0, tag: '', desc: '', part: '', maker: '', props: {} };
  switch (L.role) {
    case 'main':
      s.tag = nextTag(pr, L.prefix);
      mem[L.prefix] = s.tag;
      if (SOLENOID_VALVES[type]) s.props.sol = mem[prefixFor('sol', standardOf(pr))] || '';
      break;
    case 'aux':
      s.tag = mem[L.prefix] || mem[prefixFor('coil', standardOf(pr))] || allSyms(pr).find((o) => o.type === 'coil')?.tag || L.prefix + '1';
      break;
    case 'plc':
      s.tag = allSyms(pr).find((o) => LIB[o.type]?.role === 'plc')?.tag || 'A1';
      s.props.addr = nextPlcAddress(pr, type);
      break;
    case 'term':
      s.tag = mem.X || 'X1';
      s.props.pinNo = nextTerminal(pr, s.tag);
      break;
    case 'pot': {
      if (type === 'earth') {
        s.props = { name: 'PE', ptype: 'PE' };
        break;
      }
      const used = new Set(page.syms.filter((o) => o.type === 'pot').map((o) => o.props.name));
      const nm = ['L1', 'L2', 'L3', 'N', 'PE', '+24V', '0V'].find((n) => !used.has(n)) || 'P1';
      s.props = { name: nm, ptype: ptypeFromName(nm) };
      break;
    }
    case 'brk':
      s.props = { name: '1L', ptype: 'L' };
      break;
    case 'text':
      s.props = { text: 'Metin', size: 3.5 };
      break;
    case 'sl':
      // single-line symbols refer to an existing device: start from the last one used
      s.tag = L.prefix ? mem[L.prefix] || '' : '';
      break;
    case 'box':
      s.props = { w: 120, h: 80 };
      break;
    case 'cable':
      s.tag = nextTag(pr, 'W');
      s.props = { len: 32, cableType: 'NYY-J', cores: '1,2,3,PE', cs: '2.5' };
      break;
  }
  return s;
}

/** Normalises a tag typed by a user: "-k 1" → "K1". */
export const normaliseTag = (v: string) => v.replace(/^[-=+\s]+/, '').replace(/\s+/g, '').toUpperCase();
